import { useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import useIsMobile from '@/hooks/useIsMobile';

interface PhotoGalleryModalProps {
  images: string[];
  open: boolean;
  initialIndex: number;
  onClose: () => void;
}

export default function PhotoGalleryModal({ images, open, initialIndex, onClose }: PhotoGalleryModalProps) {
  const isMobile = useIsMobile(640);
  const [current, setCurrent] = useState(initialIndex);
  const [imgOpacity, setImgOpacity] = useState(1);
  const activeThumbnailRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) setCurrent(initialIndex);
  }, [open, initialIndex]);

  // Scroll active thumbnail into view
  useEffect(() => {
    activeThumbnailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [current]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') switchTo((current + 1) % images.length);
      if (e.key === 'ArrowLeft') switchTo((current - 1 + images.length) % images.length);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, current, images.length]);

  if (!images.length) return null;

  const switchTo = (index: number) => {
    setImgOpacity(0);
    setTimeout(() => {
      setCurrent(index);
      setImgOpacity(1);
    }, 140);
  };

  const currentSrc = images[current];

  // ── Thumbnail strip (vertical on desktop, horizontal on mobile) ──
  const thumbStrip = (
    <div style={{
      ...(isMobile
        ? { height: 80, overflowX: 'auto', overflowY: 'hidden', display: 'flex', flexDirection: 'row', gap: 6, padding: '8px 12px', background: '#111', flexShrink: 0 }
        : { width: 140, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 6, padding: 8, background: '#111', flexShrink: 0 }
      ),
      scrollbarWidth: 'thin',
      scrollbarColor: '#444 transparent',
    }}>
      {images.map((src, i) => (
        <button
          key={i}
          ref={i === current ? activeThumbnailRef : undefined}
          onClick={() => switchTo(i)}
          style={{
            all: 'unset', cursor: 'pointer', flexShrink: 0,
            borderRadius: 6, overflow: 'hidden',
            outline: i === current ? '2px solid #fff' : '2px solid transparent',
            outlineOffset: -2,
            transition: 'outline 0.15s',
            ...(isMobile ? { height: '100%', aspectRatio: '4/3' } : {}),
          }}
        >
          <img
            src={src}
            alt={`thumb-${i}`}
            style={{
              width: isMobile ? 'auto' : '100%',
              height: isMobile ? '100%' : 'auto',
              aspectRatio: '4/3',
              objectFit: 'cover',
              display: 'block',
              opacity: i === current ? 1 : 0.55,
              transition: 'opacity 0.15s',
            }}
          />
        </button>
      ))}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="92vw"
      centered
      styles={{
        body: { padding: 0 },
        mask: { backdropFilter: 'blur(4px)' },
      }}
      closeIcon={false}
    >
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: isMobile ? '90vh' : '85vh', background: '#0a0a0a', borderRadius: 8, overflow: 'hidden' }}>

        {/* ── Main image area ──────────────────────────────── */}
        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', minHeight: 0 }}>
          {/* Blur background */}
          <img
            src={currentSrc}
            aria-hidden
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', filter: 'blur(24px) brightness(0.25)', transform: 'scale(1.1)',
            }}
          />
          {/* Current image */}
          <img
            src={currentSrc}
            alt={`photo-${current + 1}`}
            style={{
              position: 'relative', zIndex: 1,
              maxWidth: '100%', maxHeight: '100%',
              objectFit: 'contain',
              opacity: imgOpacity,
              transition: 'opacity 0.14s ease',
              borderRadius: 4,
            }}
          />

          {/* Prev button */}
          <button
            onClick={() => switchTo((current - 1 + images.length) % images.length)}
            style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              zIndex: 2, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%',
              width: 44, height: 44, cursor: 'pointer', color: '#fff', fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.8)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.55)')}
          >
            <LeftOutlined />
          </button>

          {/* Next button */}
          <button
            onClick={() => switchTo((current + 1) % images.length)}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              zIndex: 2, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%',
              width: 44, height: 44, cursor: 'pointer', color: '#fff', fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.8)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.55)')}
          >
            <RightOutlined />
          </button>

          {/* Counter + Close */}
          <div style={{ position: 'absolute', top: 14, left: 0, right: 0, zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 14px' }}>
            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, background: 'rgba(0,0,0,0.45)', padding: '4px 12px', borderRadius: 20, fontWeight: 500 }}>
              {current + 1} / {images.length}
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%',
                width: 36, height: 36, cursor: 'pointer', color: '#fff', fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* ── Thumbnail strip ──────────────────────────────── */}
        {thumbStrip}
      </div>
    </Modal>
  );
}
